/**
 * Encryption Helper for Secure Cross-App Authentication
 * Uses AES-GCM encryption with Web Crypto API
 * 
 * Security: Credentials encrypted before passing in URL
 * No backend changes required
 */

// Shared secret key (base64 encoded)
// In production, you can rotate this key as needed
const SECRET_KEY = 'TDTLSecureKey2025ProjectUnifiedAuth';

/**
 * Convert string to ArrayBuffer
 */
function str2ab(str) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

/**
 * Convert ArrayBuffer to base64
 */
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary_string = atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Get crypto key from secret
 */
async function getCryptoKey() {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(SECRET_KEY),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('tdtl-salt-2025'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Check if encryption is supported
 */
export function isEncryptionSupported() {
  // Check for Web Crypto API
  if (!window.crypto || !window.crypto.subtle) {
    console.warn('[Encryption] Web Crypto API not supported');
    return false;
  }

  // Check for HTTPS in production
  if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
    console.warn('[Encryption] HTTPS required for encryption in production');
    return false;
  }

  return true;
}

/**
 * Encrypt credentials object
 * @param {Object} data - { email, password, role }
 * @returns {Promise<string>} Encrypted base64 string
 */
export async function encryptCredentials(data) {
  try {
    if (!isEncryptionSupported()) {
      throw new Error('Encryption not supported in this environment');
    }

    console.log('[Encryption] Encrypting credentials...');
    
    const key = await getCryptoKey();
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // Initialization vector

    const dataString = JSON.stringify(data);
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encoder.encode(dataString)
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    const encryptedString = arrayBufferToBase64(combined.buffer);
    console.log('[Encryption] Successfully encrypted');
    
    return encryptedString;
  } catch (error) {
    console.error('[Encryption] Encryption failed:', error);
    throw error;
  }
}

/**
 * Decrypt credentials string
 * @param {string} encryptedData - Base64 encrypted string
 * @returns {Promise<Object>} Decrypted { email, password, role }
 */
export async function decryptCredentials(encryptedData) {
  try {
    if (!isEncryptionSupported()) {
      throw new Error('Decryption not supported in this environment');
    }

    console.log('[Encryption] Decrypting credentials...');
    
    const key = await getCryptoKey();
    const combined = new Uint8Array(base64ToArrayBuffer(encryptedData));
    
    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encrypted
    );

    const decoder = new TextDecoder();
    const dataString = decoder.decode(decrypted);
    const data = JSON.parse(dataString);
    
    console.log('[Encryption] Successfully decrypted');
    
    return data;
  } catch (error) {
    console.error('[Encryption] Decryption failed:', error);
    throw error;
  }
}

/**
 * Simple fallback encryption for unsupported browsers
 * WARNING: Not secure, only for obfuscation
 */
export function simpleFallbackEncrypt(data) {
  console.warn('[Encryption] Using fallback (not secure!)');
  const str = JSON.stringify(data);
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * Simple fallback decryption
 */
export function simpleFallbackDecrypt(encryptedData) {
  console.warn('[Encryption] Using fallback (not secure!)');
  const str = decodeURIComponent(escape(atob(encryptedData)));
  return JSON.parse(str);
}

